import { makeAutoObservable } from 'mobx'

/** Ключ у `localStorage` — стан згортання бокового меню (`SPEC-014` §1.1). */
export const SIDER_COLLAPSED_KEY = 'wl-sider-collapsed'

/** До якої ширини вікна вигляд мобільний — поріг легасі (`DefaultLayoutStore.js:26-28`). */
export const MOBILE_MAX_WIDTH = 670

const MOBILE_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`

/**
 * Чи згорнуто меню за збереженим вибором.
 *
 * Уперше — згорнуте, як у легасі (`DefaultLayout.jsx:47`): так вміст
 * отримує ширину, а меню розгортається одним кліком.
 */
const readCollapsed = (): boolean => {
    try {
        const value = window.localStorage.getItem(SIDER_COLLAPSED_KEY)

        return value === null ? true : value === 'true'
    } catch {
        // Сховище недоступне (приватний режим) — як за першого відкриття
        return true
    }
}

const readIsMobile = (): boolean => {
    try {
        return window.matchMedia(MOBILE_QUERY).matches
    } catch {
        return false
    }
}

/**
 * Каркас застосунку: бокове меню й мобільний вигляд.
 *
 * ⚠️ Стан згортання живе в браузері, а не на акаунті: його треба знати до
 * першого запиту, інакше кожне відкриття починалося б зі стрибка ширини
 * вмісту. У PD він жив у `useState` і губився з перезавантаженням.
 *
 * Мобільний режим (≤ 670 px) рахує `matchMedia`, а не ширина з `resize`: подія
 * приходить лише на перетині порога, а не на кожен піксель.
 */
class LayoutManager {
    isCollapsed: boolean = readCollapsed()

    isMobile: boolean = readIsMobile()

    /** Меню в мобільному вигляді — шухляда зліва (`SPEC-014` §1.4). */
    isMobileMenuOpen = false

    /** Пошук у мобільному вигляді — поле на всю ширину під шапкою. */
    isMobileSearchOpen = false

    constructor() {
        makeAutoObservable(this)

        try {
            window.matchMedia(MOBILE_QUERY).addEventListener('change', (event) => this.setIsMobile(event.matches))
        } catch {
            // Без `matchMedia` (тести) лишається вигляд, визначений на старті
        }
    }

    toggleCollapsed(): void {
        this.setCollapsed(!this.isCollapsed)
    }

    setCollapsed(value: boolean): void {
        this.isCollapsed = value

        try {
            window.localStorage.setItem(SIDER_COLLAPSED_KEY, String(value))
        } catch {
            // Не запам'ятається між відкриттями — але згорнеться
        }
    }

    setIsMobile(value: boolean): void {
        this.isMobile = value

        // Шухляди мобільного вигляду на широкому екрані не лишаються висіти
        if (!value) {
            this.isMobileMenuOpen = false
            this.isMobileSearchOpen = false
        }
    }

    setMobileMenuOpen(value: boolean): void {
        this.isMobileMenuOpen = value
    }

    toggleMobileSearch(): void {
        this.isMobileSearchOpen = !this.isMobileSearchOpen
    }
}

export const layoutManager = new LayoutManager()
